/**
 * deorum-retailer-cta.js
 *
 * Keeps blocks/deorum-retailer-cta.liquid in step with the selected variant.
 *
 * On a product page the variant picker morphs only itself - see
 * fetchUpdatedSection in variant-picker.js, which passes no morph target for a
 * plain product page - and hands every other component the freshly fetched
 * document on a variantUpdate event. So the CTA re-renders itself from that
 * document, the same way product-price.js and product-inventory.js do.
 *
 * The block always emits its host element and expresses "no CTA" as having no
 * children, because morph() with childrenOnly returns before it ever copies
 * attributes onto the root: anything kept in a host attribute, class or hidden
 * flag would go stale on the first variant switch.
 *
 * Loaded from snippets/scripts.liquid rather than from inside the block. Two
 * reasons: a script tag inside the host would itself be a child, which defeats
 * the CSS that hides an empty host, and quick-add.js builds its dialog by
 * cloning nodes out of a DOMParser document, where script tags never run - so
 * a block-local script would leave the element undefined on collection pages.
 */

import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { Component } from '@theme/component';

class DeorumRetailerCta extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updateCta);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updateCta);
  }

  /**
   * Re-renders the CTA for the newly selected variant.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updateCta = (event) => {
    if (event.detail.data.newProduct) {
      // Combined listing: the picker swapped in a different product.
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      // An update for some other product on the page. Not ours.
      return;
    }

    // No variant resolves for the chosen options. The server falls back to the
    // first available variant, so the fetched markup describes something the
    // shopper did not pick - say nothing rather than something wrong.
    if (event.detail.resource == null) {
      this.replaceChildren();
      return;
    }

    // The fetched document is the whole product page, so scope by block id:
    // morph moves matched nodes out of it, and there may be more than one of us.
    const newCta = event.detail.data.html.querySelector(
      `deorum-retailer-cta[data-block-id="${this.dataset.blockId}"]`
    );

    if (!newCta) return;

    morph(this, newCta, { childrenOnly: true });
  };
}

if (!customElements.get('deorum-retailer-cta')) {
  customElements.define('deorum-retailer-cta', DeorumRetailerCta);
}
